import { ChildModel, ModelBase } from 'store/model-base';
import { ActivationStatus, ActivationType, ActivationVerificationStatus, Carriers } from 'my-account/constants';
import { ActivationPortInfo } from './port-info';

export class ActivationInfo extends ModelBase {
    public static storeName: string = 'activationInfo';
    public static idAttribute: string = 'id.key';
    private static ACTIONABLE_CODES: string[] = [ 'R671', 'R678', 'R679', 'R680', 'R681', 'R682', 'R683', 'R615', 'R616', 'R657'];
    private static COMPLEXITY_ERROR: string = 'R664';
    private static DISPLAY_CARRIER_TELEPHONE_CODE: string[] = [ 'R615', 'R616', 'R621', 'R623', 'R657', 'R661', 'R667', 'R671' ];
    private static NON_ACTIONABLE_CBM_CODES: string[] = [ 'R651', 'R652', 'R653', 'R654', 'R655', 'R656', 'R623', 'R617', 'R618', 
        'R619', 'R620', 'R658', 'R621', 'R622', 'R623', 'R659', 'R660', 'R661', 'R662', 'R663', 'R664', 'R665', 'R666', 'R667', 'R668', 'R669', 'R670', 'R671', 'R672', 'R673', 'R674', 'R675', 'R676', 'R677', 'R681', 'R684' ];
    private static NON_ACTIONABLE_CBM_GEMINI_CODES: string[] = [ 'R232', 'R759', 'R10190', 'R130', 'R131', 'R256', 'R10000', 'R215', 'R278', 'R10082', 'R658', 'R10174', 'R10202', 'R283', 'R607' ];
    public id: Id;
    public iccId: string;
    public status: ActivationStatus;
    public type: ActivationType;
    public errorCode: string;
    public portInfo: ActivationPortInfo;
    public lineNumber: string;
    public additionalVerificationNeeded: boolean;
    public additionalVerificationStatus: ActivationVerificationStatus;
    public provisioningStatus: string;

    public static create<T extends ModelBase>(data: ApiResponse): T {
        if (data.lineNumber === 'unknown') {
            data.lineNumber = '';
        }

        return super.create<T>(data);
    }

    protected static get hasOne(): ChildModel[] {
        return [{
            attrName: 'portInfo',
            model: ActivationPortInfo
        }];
    }

    public get isInProgress(): boolean {
        return this.status === ActivationStatus.NEW_IN_PROGRESS || this.status === ActivationStatus.PORT_IN_PROGRESS;
    }

    public get isPending(): boolean {
        return this.status === ActivationStatus.PENDING;
    }

    public get isRFA(): boolean {
        return this.status === ActivationStatus.RFA;
    }

    public get isActive(): boolean {
        return this.status === ActivationStatus.ACTIVE;
    }

    public get isError(): boolean {
        return this.status === ActivationStatus.FAILED;
    }

    public get isNotActivated(): boolean {
        return this.status === ActivationStatus.NOT_ACTIVATED;
    }

    public get isUnknown(): boolean {
        return this.status === ActivationStatus.UNKNOWN;
    }

    public get isResolutionRequired(): boolean {
        return this.status === ActivationStatus.RESOLUTION_REQUIRED;
    }

    public get isActionableError(): boolean {
        return this.isError && ActivationInfo.ACTIONABLE_CODES.includes(this.errorCode);
    }

    public get isNonActionableError(): boolean {
        return this.isError && !ActivationInfo.ACTIONABLE_CODES.includes(this.errorCode);
    }

    public get isNonActionableCBMError(): boolean {
        return this.isError && ActivationInfo.NON_ACTIONABLE_CBM_CODES.includes(this.errorCode);
    }

    public get isNonActionableCBMGeminiError(): boolean {
        return this.isError && ActivationInfo.NON_ACTIONABLE_CBM_GEMINI_CODES.includes(this.errorCode);
    }

    public get isComplexityError(): boolean {
        return this.isError && this.errorCode === ActivationInfo.COMPLEXITY_ERROR;
    }

    public get isDisplayCarrierTelephoneError(): boolean {
        return ActivationInfo.DISPLAY_CARRIER_TELEPHONE_CODE.includes(this.errorCode);
    }

    public get isNewNumber(): boolean {
        return this.type === ActivationType.NEW;
    }

    public get isPort(): boolean {
        return this.type === ActivationType.PORT_IN;
    }

    public get isNdel(): boolean {
        return this.type === ActivationType.NDEL;
    }

    public get isLocked(): boolean {
        return this.additionalVerificationStatus === ActivationVerificationStatus.LOCKED;
    }

    public get isRetry(): boolean {
        return this.additionalVerificationStatus === ActivationVerificationStatus.RETRY;
    }

    public get isExpired(): boolean {
        return this.additionalVerificationStatus === ActivationVerificationStatus.EXPIRED;
    }

    public get isAdditionalVerificationStatusPending(): boolean {
        return this.additionalVerificationStatus === ActivationVerificationStatus.PENDING;
    }

    public get isNotStarted(): boolean {
        return this.additionalVerificationStatus === ActivationVerificationStatus.NOT_STARTED;
    }

    public get isVerified(): boolean {
        return this.additionalVerificationStatus === ActivationVerificationStatus.VERIFIED;
    }

    public get isVerizon(): boolean {
        return this.isPort && this.portInfo.carrierName === Carriers.VERIZON;
    }

    public get isActivationDirectedToApp(): boolean {
        return this.isPending || this.isError || this.isInProgress || this.isResolutionRequired || this.isUnknown || this.isNotActivated;
    }
}
